/**
 * The one way the app talks to the backend.
 *
 * Requests go to `/api/...` on the app's own origin (the dev server or the
 * production proxy forwards them to the backend), so the HttpOnly session
 * cookie travels with every call. A 401 means the learner is no longer signed
 * in; the auth layer is told so it can send them to sign-in.
 */
import { API_BASE_URL } from '../config';

type UnauthorizedHandler = () => void;

let unauthorizedHandler: UnauthorizedHandler | null = null;

/** Register the callback run when any API call comes back 401. */
export function setUnauthorizedHandler(
  handler: UnauthorizedHandler | null
): void {
  unauthorizedHandler = handler;
}

export class UnauthorizedError extends Error {
  constructor() {
    super('Sign in required');
    this.name = 'UnauthorizedError';
  }
}

export interface ApiFetchOptions {
  /**
   * Whether a 401 tells the auth layer the learner is signed out (default true).
   * The auth endpoints themselves turn this off: their callers handle 401.
   */
  notifyUnauthorized?: boolean;
}

/**
 * `fetch` against the API with the session cookie included. JSON bodies get a
 * `Content-Type: application/json` header unless the body is `FormData`.
 */
export async function apiFetch(
  path: string,
  init: RequestInit = {},
  { notifyUnauthorized = true }: ApiFetchOptions = {}
): Promise<Response> {
  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string> | undefined),
  };
  if (
    init.body !== undefined &&
    !(init.body instanceof FormData) &&
    !('Content-Type' in headers)
  ) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers,
    credentials: 'include',
  });

  if (response.status === 401 && notifyUnauthorized) {
    unauthorizedHandler?.();
  }
  return response;
}

/** The API's error message for a failed response (`detail` or `message`), else its status text. */
export async function errorMessageFrom(response: Response): Promise<string> {
  try {
    const data = (await response.json()) as unknown;
    if (typeof data === 'string') return data;
    if (data && typeof data === 'object') {
      const { detail, message } = data as {
        detail?: unknown;
        message?: unknown;
      };
      if (typeof detail === 'string') return detail;
      if (typeof message === 'string') return message;
    }
  } catch {
    // Not JSON; fall back to the status text.
  }
  return response.statusText;
}
